import { useId, useState, type ReactNode } from 'react';
import { CloseRounded, InfoOutlined } from '@mui/icons-material';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { designTokens } from '../tokens';
import { AnchoredPopover } from './AnchoredOverlay';

export type HelpPopoverProps = {
  title: string;
  children: ReactNode;
};

/** 필요한 설명을 제목 옆에서 열고 닫는 공용 도움말입니다. 업무 내용은 호출자가 소유합니다. */
export function HelpPopover({ title, children }: HelpPopoverProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const id = `help-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const titleId = `${id}-title`;
  const descriptionId = `${id}-content`;
  const label = `${title} 도움말`;
  const close = () => setAnchor(null);
  if (children === undefined || children === null || typeof children === 'boolean' || children === '') return null;

  return <>
    <Tooltip title={label}>
      <IconButton
        type="button" size="small" aria-label={label}
        aria-haspopup="dialog" aria-expanded={Boolean(anchor)} aria-controls={anchor ? id : undefined}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{
          flexShrink: 0, color: designTokens.color.secondaryText,
          width: designTokens.button.sizes.small.height, height: designTokens.button.sizes.small.height,
          minWidth: designTokens.button.sizes.small.height, p: 0,
          '@media (pointer: coarse)': { width: designTokens.layout.touchTargetMinSize, height: designTokens.layout.touchTargetMinSize, minWidth: designTokens.layout.touchTargetMinSize },
        }}
      ><InfoOutlined sx={{ fontSize: designTokens.button.sizes.small.iconSize }} /></IconButton>
    </Tooltip>
    <AnchoredPopover id={id} anchorEl={anchor} onClose={close} ariaLabelledBy={titleId} ariaDescribedBy={descriptionId} padding="standard">
      <Stack direction="row" sx={{ alignItems: 'center', gap: `${designTokens.space.xs}px`, mb: `${designTokens.space.xs}px` }}>
        <Typography id={titleId} component="h2" sx={{ ...designTokens.typography.componentTitle, flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{label}</Typography>
        <IconButton type="button" size="small" aria-label={`${label} 닫기`} onClick={close}><CloseRounded fontSize="small" /></IconButton>
      </Stack>
      <Box id={descriptionId} sx={{ ...designTokens.typography.ui, color: designTokens.color.primaryText, overflowWrap: 'anywhere', whiteSpace: 'pre-line' }}>{children}</Box>
    </AnchoredPopover>
  </>;
}
